import * as React from 'react';
import { Skin } from '../models/common/Skin';
import { CheckOutType } from '../models/common/CheckOutType';
import { CheckSummary } from '../models/common/CheckSummary';
import { PaymentGateway } from '../models/common/PaymentGateway';
import { PaymentGatewayConnector } from '../models/common/PaymentGatewayConnector';
import { PMSType } from '../models/common/PMSType';
import { POSType } from '../models/common/POSType';
import { FeedbackType } from '../models/common/FeedbackType';
import { PaymentMethodsAccepted } from '../models/payment/PaymentMethodsAccepted';

const localState = localStorage.getItem("checkout_state") !== null ? JSON.parse(localStorage.getItem("checkout_state")!) : null;

let reducer = (state: CheckOutState, newState: CheckOutState) => {
    if (newState === null) {
        localStorage.removeItem("checkout_state");
        return initialState;
    }

    return { ...state, ...newState };
};

export interface CheckOutState {
    restaurantLocale: string | null;
    restaurantId: string | null;
    restaurantName: string | null;
    restaurantLogoUrl: string | null;
    restaurantLoaderUrl: string | null;
    restaurantCustomTipButtonLogoUrl: string | null;
    restaurantCurrency: string | null;
    restaurantCountry: string | null;
    restaurantApiKey: string | null;
    applePayMerchantId: string | null;
    restaurantGooglePayMerchantId: string | null;
    restaurantGooglePayMerchantName: string | null;
    menuUrl: string | null;
    paymentMethodsAccepted: PaymentMethodsAccepted | undefined;
    paymentGateway: PaymentGateway;
    paymentGatewayConnector: PaymentGatewayConnector;
    posType: POSType | null;
    pmsType: PMSType;
    skin: Skin,
    tableNumber: string | null;
    tableId: string | null;
    checkInId: number | null;
    checkOutType: CheckOutType | null;
    receiptCheckExternalId: string | null;
    sentRating: boolean;
    checkInUrl: string | null;
    shareCheckInUrl: string | null;
    digitalServiceFee: string | null;
    checks: CheckSummary[] | null;
    stripeApiKey: string | null;
    stripeAccountId: string | null;
    spreedlyEnvKey: string | null;
    useCaptcha: boolean;
    feedbackType: FeedbackType;
    akiraSurveyUrl: string | null;
}

const initialState: CheckOutState = {
    restaurantLocale: null,
    restaurantId: null,
    restaurantName: null,
    restaurantLogoUrl: null,
    restaurantLoaderUrl: null,
    restaurantCustomTipButtonLogoUrl: null,
    restaurantCurrency: null,
    restaurantCountry: null,
    restaurantApiKey: null,
    applePayMerchantId: null,
    restaurantGooglePayMerchantId: null,
    restaurantGooglePayMerchantName: null,
    menuUrl: null,
    paymentMethodsAccepted: undefined,
    paymentGateway: PaymentGateway.Demo,
    paymentGatewayConnector: PaymentGatewayConnector.None,
    posType: null,
    pmsType: PMSType.None,
    skin: { use_custom_skin: false, primary_main_color: "#000000", primary_light_color: "#1dd5f6", primary_dark_color: "#5367df", 
        appBar_background_color: "#ffffff", custom_tip_button_background_color: "#3a9bea", custom_tip_button_text_color: "#ffffff",
        custom_tip_button_selected_background_color: "#3a9bea", custom_tip_button_selected_text_color: "#ffffff", 
        customTipButtonSelectedBorderColor: "#5367df" },
    tableNumber: null,
    tableId: null,
    checkInId: null,
    checkOutType: null,
    receiptCheckExternalId: null,
    sentRating: false,
    checkInUrl: null,
    shareCheckInUrl: null,
    digitalServiceFee: null,
    checks: null,
    stripeApiKey: null,
    stripeAccountId: null,
    spreedlyEnvKey: null,
    useCaptcha: false,
    feedbackType: FeedbackType.None,
    akiraSurveyUrl: null
};

export const CheckOutStateContext = React.createContext<{state: CheckOutState; setState: React.Dispatch<any>;}>({state: initialState, setState: () => null});

const CheckOutStateContextProvider = (props: { children?: React.ReactNode }) => {
    const [checkOutState, setCheckOutState] = React.useReducer(reducer, localState || initialState);
    
    React.useEffect(() => {
        localStorage.setItem("checkout_state", JSON.stringify(checkOutState));
    }, [checkOutState]);

    return (
        //@ts-ignore
        <CheckOutStateContext.Provider value={{ state: checkOutState, setState: setCheckOutState }}>
            {props.children}
        </CheckOutStateContext.Provider>
    );
}

export default CheckOutStateContextProvider;